'use client';

import { useReveal } from '@/hooks/use-reveal';

export default function Maison() {
  const { ref, visible } = useReveal();

  return (
    <section id="maison" ref={ref} className="relative py-28 md:py-40 bg-[#F8F1E4] overflow-hidden">
      {/* Decorative grain overlay */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(43,24,16,0.5) 1px, transparent 0)',
          backgroundSize: '24px 24px',
        }}
      />

      <div className="container mx-auto px-6 relative">
        <div className="max-w-4xl mx-auto text-center">
          {/* Section label */}
          <div className={`reveal ${visible ? 'visible' : ''} mb-8`}>
            <div className="flex items-center justify-center gap-4">
              <div className="w-10 h-px bg-[#8B4513]" />
              <span className="text-[#8B4513] font-serif-display text-sm tracking-[0.4em] uppercase">
                La Maison
              </span>
              <div className="w-10 h-px bg-[#8B4513]" />
            </div>
          </div>

          {/* Title */}
          <h2 className={`reveal ${visible ? 'visible' : ''} font-display text-[#2B1810] text-4xl md:text-6xl lg:text-7xl font-medium leading-[1.05] mb-8`}>
            Une Maison où le Cuir
            <br />
            <span className="italic text-luxe-gold">devient Émotion</span>
          </h2>

          {/* Intro paragraph */}
          <p className={`reveal ${visible ? 'visible' : ''} text-[#5C2E03]/90 text-lg md:text-xl font-serif-display font-light leading-relaxed mb-10 max-w-3xl mx-auto`}>
            ASMAO STYLE CUIR incarne l'excellence de la maroquinerie artisanale française.
            Fondée sur les valeurs transmises par les grandes maisons parisiennes, notre maison
            célèbre la rencontre entre le geste ancestral de l'artisan et la noblesse intemporelle
            du cuir véritable.
          </p>

          <p className={`reveal ${visible ? 'visible' : ''} text-[#5C2E03]/80 text-base md:text-lg font-serif-display font-light leading-relaxed mb-16 max-w-3xl mx-auto`}>
            Chaque sac, chaque ceinture, chaque portefeuille qui naît dans nos ateliers est
            l'aboutissement d'une obsession : celle de faire entrer la beauté, la patience et
            l'amour dans le quotidien de celles et ceux qui nous font confiance. Nos cuirs sont
            sélectionnés avec une exigence absolue, tannés selon des méthodes végétales
            traditionnelles, puis confiés à des mains qui ont appris le métier au fil d'une vie.
          </p>

          {/* Three pillars */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mt-16">
            {[
              {
                num: 'I',
                title: 'Sélection',
                text: "Des cuirs pleine fleur tannés végétal, choisis parmi les plus belles peaux de taurillons français et italiens, vieillis lentement pour révéler leur patine.",
              },
              {
                num: 'II',
                title: 'Façonnage',
                text: "Chaque pièce est coupée à la main, assemblée au point sellier, et finie à la cire d'abeille. Aucune chaîne de montage, aucun raccourci, jamais.",
              },
              {
                num: 'III',
                title: 'Patine',
                text: "Nos cuirs vieillissent avec vous. Le temps les embellit, leur confère une teinte unique, un toucher incomparable. Ils deviennent des compagnons de vie.",
              },
            ].map((pillar, idx) => (
              <div
                key={pillar.title}
                className={`reveal ${visible ? 'visible' : ''} group relative text-center px-6`}
                style={{ transitionDelay: `${idx * 150}ms` }}
              >
                <div className="font-display text-6xl text-[#C9A961]/40 mb-4 group-hover:text-[#C9A961] transition-colors duration-700">
                  {pillar.num}
                </div>
                <h3 className="font-display text-2xl text-[#2B1810] mb-4 tracking-wide">
                  {pillar.title}
                </h3>
                <p className="text-[#5C2E03]/75 text-base font-serif-display leading-relaxed font-light">
                  {pillar.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
